// "Directions" on a forecourt's card: hand the trip to the phone's own maps app.
//
// Asked for by a reader alongside #96. The site does not route anybody: it has
// no routing engine, and the reader's maps app already knows where they are,
// how they travel and what the traffic is doing. So this is one link, with the
// destination filled in and nothing else — the site never asks for or sees the
// reader's location, and needs no permission to offer it.
//
// Which app: Apple Maps on an iPhone or iPad, Google Maps everywhere else. On
// Android the Google URL opens the Maps app straight into directions; on a
// desktop it is the website. Coordinates rather than the forecourt's name,
// because "Tesco" is a search and a lat/lon is a place.
//
// Imports nothing that touches `import.meta.env`, so node --test can load it
// (scripts/test-directions.mjs) — live.ts, which draws the card, cannot be.

import { escapeHtml } from './escape.ts';

export type MapsApp = 'apple' | 'google';

/**
 * Which maps app this device hands directions to.
 *
 * iPadOS 13 and later report a desktop Mac user agent by default, so a
 * "Macintosh" with a touchscreen is an iPad. A real Mac has no touch points
 * and gets Google: Apple Maps on the web is the weaker of the two there, and
 * the request was for phones.
 */
export function mapsAppFor(userAgent: string, maxTouchPoints = 0): MapsApp {
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'apple';
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return 'apple';
  return 'google';
}

/** The app's own directions URL to a point, driving, from wherever the reader is. */
export function directionsUrl(lat: number, lon: number, app: MapsApp): string {
  // Six decimal places is about 10 cm, finer than any forecourt needs.
  const to = `${lat.toFixed(6)},${lon.toFixed(6)}`;
  return app === 'apple'
    ? `https://maps.apple.com/?daddr=${to}&dirflg=d`
    : `https://www.google.com/maps/dir/?api=1&destination=${to}&travelmode=driving`;
}

/**
 * The link itself, or '' when there is nowhere to send the reader — a
 * feature at 0,0 or with no position is a missing value, not the Gulf of
 * Guinea.
 */
export function directionsLink(lat: number, lon: number, app: MapsApp): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return '';
  const label = app === 'apple' ? 'Directions in Apple Maps' : 'Directions in Google Maps';
  return `<a class="inc-directions" href="${escapeHtml(directionsUrl(lat, lon, app))}"
     target="_blank" rel="noopener noreferrer" aria-label="${label}">Directions ↗</a>`;
}

/** This browser's maps app; Google when there is no navigator (Node, prerender). */
export function deviceMapsApp(): MapsApp {
  if (typeof navigator === 'undefined') return 'google';
  return mapsAppFor(navigator.userAgent ?? '', navigator.maxTouchPoints ?? 0);
}
